import { useEffect, useState } from 'react';
import {
  getProductsGuides,
  getProductsImages,
  queryProducts,
} from '../../../../../services/products/productService';
import { getBase64Img } from '../../../../../utils/format';
import { DbProduct } from './types';

/**
 * Leituras de produto usadas pela tela de pedido, sobre as rotas que o portal
 * já tem: /queryProducts, /productsImages e /productsGuides.
 */

/** O que o doGet devolve: a página no sucesso, `{ httpStatus, body }` no erro. */
type RawPage = {
  rows?: number;
  data?: DbProduct[];
  httpStatus?: number;
  body?: { message?: string; error?: string } | null;
} | null;

export const readPage = (resp: RawPage) => {
  if (resp && Array.isArray(resp.data)) {
    return { total: Number(resp.rows ?? resp.data.length), items: resp.data };
  }
  if (resp?.httpStatus) {
    const detail = resp.body?.message ?? resp.body?.error ?? '';
    throw new Error(
      `A API respondeu ${resp.httpStatus}${detail ? `: ${detail}` : ''}`
    );
  }
  return { total: 0, items: [] as DbProduct[] };
};

export const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

/**
 * Um produto pelo id. Não usa o getProductById do portal: apesar do nome, ele
 * passa o valor no parâmetro de código SAP.
 */
export const fetchProduct = async (id: number): Promise<DbProduct | null> =>
  readPage(
    await queryProducts(
      String(id),
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      '0',
      1
    )
  ).items[0] ?? null;

/** Produto (nome, categoria e textos descritivos) direto do banco. */
export const useProduct = (id: number) => {
  const [product, setProduct] = useState<DbProduct | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchProduct(id).then(
      (found) => {
        if (!cancelled) setProduct(found);
      },
      (failure: unknown) => {
        if (!cancelled) setError(errorMessage(failure));
      }
    );
    return () => {
      cancelled = true;
    };
  }, [id]);

  return { product: product?.id === id ? product : null, error };
};

/**
 * /productsImages devolve só as imagens, sem dizer de qual código é cada uma.
 * Por isso a busca é um código por vez, com cache para não repetir a chamada.
 */
const imageCache = new Map<string, Promise<string | null>>();

export const fetchProductImage = (sapCode: string) => {
  let cached = imageCache.get(sapCode);
  if (!cached) {
    cached = getProductsImages([sapCode]).then(
      (images: string[]) => (images?.[0] ? getBase64Img(images[0]) : null),
      () => null
    );
    imageCache.set(sapCode, cached);
  }
  return cached;
};

/** Primeira foto do produto, como data URL; `null` enquanto carrega ou sem foto. */
export const useProductImage = (sapCode?: string) => {
  const [image, setImage] = useState<{ code: string; src: string | null }>();

  useEffect(() => {
    if (!sapCode) return;
    let cancelled = false;
    fetchProductImage(sapCode).then((src) => {
      if (!cancelled) setImage({ code: sapCode, src });
    });
    return () => {
      cancelled = true;
    };
  }, [sapCode]);

  return sapCode && image?.code === sapCode ? image.src : null;
};

export type ProductGuide = { productCode: string; guide: string };

/** PDFs do produto (aba Arquivos), no mesmo formato do DescriptionTile. */
export const useProductGuides = (sapCode?: string) => {
  const [state, setState] = useState<{
    code?: string;
    guides: ProductGuide[];
    loading: boolean;
  }>({ guides: [], loading: false });

  useEffect(() => {
    if (!sapCode) return;
    let cancelled = false;
    setState({ code: sapCode, guides: [], loading: true });
    getProductsGuides([sapCode]).then(
      (files: Array<{ product_code: string; guides?: string[] }>) => {
        if (cancelled) return;
        const guides = (Array.isArray(files) ? files : []).flatMap((file) =>
          (file.guides ?? []).map((guide) => ({
            productCode: file.product_code,
            guide,
          }))
        );
        setState({ code: sapCode, guides, loading: false });
      },
      () => {
        if (!cancelled) setState({ code: sapCode, guides: [], loading: false });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [sapCode]);

  return state.code === sapCode
    ? state
    : { guides: [], loading: Boolean(sapCode) };
};
